import type { OpenTextSummary } from '@/contracts/models/open-text-summary';
import type { ReportFilter } from '@/features/reporting/types/reporting.types';
import { SurveyDto, SurveyStatus } from '@/contracts/models/SurveyDto';
import {
  GenerateAiSurveyRequest,
  LoadSurveySummariesRequest,
} from '@/contracts/requests/surveyRequests';
import {
  GenerateAiSurveyResponse,
  LoadSurveySummariesResponse,
} from '@/contracts/responses/surveyResponses';
import { apiClient } from '@/services/clients/axiosClient';
import { loggingUtility } from '@/utils/loggingUtility';

// Survey Metadata types
export interface SurveyMetadata {
  title?: string;
  createdBy: string;
  updatedAt: string;
  startDate: string;
  endDate: string;
  questionCount: number;
  status: SurveyStatus;
  projects?: string[];
  description?: string;
  completionMessage?: string;
  isVotingEnabled: boolean;
  aiModerationEnabled: boolean;
  hasMixerQuestions: boolean;
  mixerQuestionText?: string;
}

// Mixer-related types
export interface MixerAnswerModeration {
  answerId: string;
  answerText: string;
  moderationStatus: 'pending' | 'approved' | 'flagged' | null;
  visible: boolean;
  moderationReason?: string | null;
  createdAt: string;
  participantName: string;
  voteCount: number;
}

// Survey template types
export interface SurveyTemplate {
  id: string;
  surveyName: string;
}

// Public API functions
export const getSurvey = async (surveyId: string): Promise<SurveyDto> => {
  const response = await apiClient.get<{ data: SurveyDto }>(
    `/surveys/${surveyId}`
  );

  return response.data;
};

// Generate AI survey
export const generateAiSurvey = async (
  request: GenerateAiSurveyRequest
): Promise<GenerateAiSurveyResponse> => {
  try {
    const response = await apiClient.post<GenerateAiSurveyResponse>(
      '/surveys/generate-ai',
      request
    );

    return response;
  } catch (error) {
    loggingUtility.error('Error generating AI survey:', error);
    throw error;
  }
};

/**
 * Store a survey (new or updated) with metadata and SurveyJS JSON data
 */
export const storeSurvey = async (
  surveyData: SurveyDto
): Promise<{ surveyId: string }> => {
  try {
    const response = await apiClient.post<{ surveyId: string }>(
      '/surveys/store',
      surveyData
    );

    return response;
  } catch (error) {
    loggingUtility.error('Error storing survey:', error);
    throw error;
  }
};

/**
 * Duplicate a survey. Backend handles ID regeneration and mixer question mapping.
 */
export enum DuplicationSourceType {
  Survey = 'survey',
  Collection = 'collection',
  Template = 'template',
}

export interface DuplicateSurveyRequest {
  sourceId: string;
  type: DuplicationSourceType;
  name?: string;
}

export const duplicateSurvey = async (
  request: DuplicateSurveyRequest
): Promise<{ surveyId: string }> => {
  try {
    const response = await apiClient.post<{ surveyId: string }>(
      '/surveys/duplicate',
      request
    );
    return response;
  } catch (error) {
    loggingUtility.error('Error duplicating survey:', error);
    throw error;
  }
};

// Load survey templates
export const loadSurveyTemplates = async (): Promise<{
  templates: SurveyTemplate[];
}> => {
  try {
    const response = await apiClient.get<{ templates: SurveyTemplate[] }>(
      '/surveys/templates'
    );
    return response;
  } catch (error) {
    loggingUtility.error('Error loading survey templates:', error);
    throw error;
  }
};

// Create survey from pre-built template (uses the duplicate endpoint)
export const createSurveyFromTemplate = async (
  templateId: string
): Promise<{ surveyId: string }> => {
  try {
    const response = await apiClient.post<{ surveyId: string }>(
      '/surveys/duplicate',
      { sourceId: templateId, type: DuplicationSourceType.Template }
    );

    return response;
  } catch (error) {
    loggingUtility.error('Error creating survey from template:', error);
    throw error;
  }
};

// Load survey summaries
export const loadSurveySummaries = async (
  filters?: LoadSurveySummariesRequest
): Promise<LoadSurveySummariesResponse> => {
  try {
    const response = await apiClient.post<LoadSurveySummariesResponse>(
      '/surveys/summaries',
      filters || {}
    );
    return response;
  } catch (error) {
    loggingUtility.error('Error loading survey summaries:', error);
    throw error;
  }
};

export const updateSurveyArchiveStatus = async (
  surveyId: string,
  action: 'archive' | 'unarchive'
): Promise<void> => {
  try {
    const status = action === 'archive' ? 'archived' : 'unarchived';
    await storeSurvey({ surveyId, status } as SurveyDto);
  } catch (error) {
    loggingUtility.error(`Error ${action}ing survey:`, error);
    throw error;
  }
};

// Reporting API - Tab-specific endpoints

/**
 * Load survey metadata (Header Summary)
 * Includes: creator, dates, status, projects, description
 */
export const getSurveyMetadata = async (
  surveyId: string
): Promise<SurveyMetadata> => {
  try {
    const response = await apiClient.get<SurveyMetadata>(
      `/surveys/${surveyId}/reporting/metadata`
    );
    return response;
  } catch (error) {
    loggingUtility.error('Error loading survey metadata:', error);
    throw error;
  }
};

/**
 * Load analytics data (Tab 1: Analytics)
 * Includes: metrics, engagement over time
 */
export const getSurveyAnalytics = async (surveyId: string) => {
  try {
    const response = await apiClient.get(
      `/surveys/${surveyId}/reporting/analytics`
    );
    return response;
  } catch (error) {
    loggingUtility.error('Error loading survey analytics:', error);
    throw error;
  }
};

/**
 * Load questions list (Tab 2: Questions)
 * Includes: question metadata, completion stats (NO chart data)
 */
export const getSurveyQuestionsList = async (surveyId: string) => {
  try {
    const response = await apiClient.get(
      `/surveys/${surveyId}/reporting/questions`
    );

    return response;
  } catch (error) {
    loggingUtility.error('Error loading survey questions list:', error);
    throw error;
  }
};

/**
 * Load questions with chart data (Tab 3: Survey Results)
 * Includes: question metadata, answer distributions, chart data.
 * Optional include filters recompute every chart over the matching cohort;
 * an empty array returns everyone.
 */
export const getSurveyQuestionsWithCharts = async (
  surveyId: string,
  filters: ReportFilter[] = []
) => {
  try {
    const response = await apiClient.post(
      `/surveys/${surveyId}/reporting/questions-charts`,
      { filters }
    );

    return response;
  } catch (error) {
    loggingUtility.error('Error loading survey questions with charts:', error);
    throw error;
  }
};

/**
 * Load mixer answers (Tab 4: Mixer Results)
 * Includes: mixer answers with votes and moderation status
 * Supports pagination, sorting, and filtering
 */
export const getSurveyMixerAnswers = async (
  surveyId: string,
  page: number = 0,
  pageSize: number = 10,
  sortField?: string,
  sortOrder?: 'asc' | 'desc',
  searchText?: string,
  moderationStatus?: string,
  visibility?: string,
  signal?: AbortSignal
) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (searchText) params.append('searchText', searchText);
    if (moderationStatus) params.append('moderationStatus', moderationStatus);
    if (visibility) params.append('visibility', visibility);

    const response = await apiClient.get<{ totalCount: number }>(
      `/surveys/${surveyId}/reporting/mixer-answers?${params.toString()}`,
      { signal }
    );

    return response;
  } catch (error) {
    loggingUtility.error('Error loading survey mixer answers:', error);
    throw error;
  }
};

/**
 * Get open text summary for a question
 * Returns AI-generated summary with key insights and topic analysis
 */
export const getOpenTextSummary = async (
  surveyId: string,
  questionId: string
) => {
  try {
    const response = await apiClient.get<{ summary: OpenTextSummary }>(
      `/surveys/${surveyId}/reporting/questions/${questionId}/open-text-summary`
    );

    return response;
  } catch (error) {
    loggingUtility.error('Error loading open text summary:', error);
    throw error;
  }
};

/**
 * Update mixer answer visibility (hide/show)
 */
export const updateMixerAnswerVisibility = async (
  surveyId: string,
  answerId: string,
  visible: boolean,
  reason?: string
): Promise<{ success: boolean }> => {
  try {
    const response = await apiClient.patch<{ success: boolean }>(
      `/surveys/${surveyId}/mixer-answers/${answerId}`,
      { visible, reason }
    );

    return response;
  } catch (error) {
    loggingUtility.error('Error updating mixer answer visibility:', error);
    throw error;
  }
};

/**
 * Export survey responses as CSV with real-time progress tracking
 *
 * Implementation details:
 * - Uses responseType: 'text' to access xhr.responseText during streaming for progress tracking
 * - Parses progress updates embedded as CSV comments (# PROGRESS:{...})
 * - Converts final text content to Blob for browser download via URL.createObjectURL()
 * - Backend sends Content-Type: text/csv; charset=utf-8 for proper Unicode handling
 *
 * @param surveyId - The ID of the survey to export
 * @param onProgress - Optional callback for progress updates
 * @returns Object containing blob for download, filename, and total record count
 */
export const exportSurveyResponses = async (
  surveyId: string,
  onProgress?: (progress: {
    processed: number;
    total: number;
    remaining: number;
    percentage: number;
    status: string;
    elapsed: number;
  }) => void
): Promise<{ blob: Blob; filename: string; totalRecords: number }> => {
  try {
    // Use axios with onDownloadProgress to process chunks as they arrive
    let csvContent = '';
    let buffer = '';
    let lastProcessedLength = 0;
    let headersFromXhr: { filename?: string; totalRecords?: number } = {};

    const response = await apiClient.get<{
      data: string;
      headers: Record<string, string>;
    }>(`/surveys/${surveyId}/export`, {
      // Use 'text' responseType to access xhr.responseText during streaming.
      // The backend sends 'Content-Type: text/csv; charset=utf-8' to ensure proper encoding.
      responseType: 'text',
      onDownloadProgress: (progressEvent: { event?: ProgressEvent }) => {
        if (!progressEvent.event) return;

        const xhr = progressEvent.event.target as XMLHttpRequest;
        const responseText = xhr.responseText;

        // Extract headers from XHR on first call (headers are available during streaming)
        if (!headersFromXhr.filename) {
          const contentDisposition = xhr.getResponseHeader(
            'content-disposition'
          );
          const totalRecordsHeader = xhr.getResponseHeader('x-total-records');

          if (contentDisposition) {
            // Parse filename from Content-Disposition
            const encodedMatch = contentDisposition.match(
              /filename\*=(?:UTF-8|iso-8859-1)?''([^;]+)/i
            );
            if (encodedMatch) {
              try {
                headersFromXhr.filename = decodeURIComponent(encodedMatch[1]);
              } catch {
                // Fall through
              }
            }

            if (!headersFromXhr.filename) {
              const filenameMatch = contentDisposition.match(
                /filename=(?:"([^"]+)"|([^;,\s]+))/i
              );
              if (filenameMatch) {
                headersFromXhr.filename = filenameMatch[1] || filenameMatch[2];
              }
            }
          }

          if (totalRecordsHeader) {
            headersFromXhr.totalRecords = parseInt(totalRecordsHeader, 10);
          }
        }

        // Process only new data since last update
        const newData = responseText.substring(lastProcessedLength);
        if (!newData) return;

        lastProcessedLength = responseText.length;
        buffer += newData;

        // Look for progress updates (sent as comments in CSV)
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('# PROGRESS:')) {
            // Parse and emit progress update
            try {
              const progressData = JSON.parse(line.substring(11));
              if (onProgress) {
                onProgress(progressData);
              }
            } catch (error) {
              loggingUtility.warn('Failed to parse progress update', error);
            }
          } else if (line) {
            // Add actual CSV content (skip progress comments)
            csvContent += line + '\n';
          }
        }
      },
    });

    // Process any remaining buffer
    if (buffer) {
      const lines = buffer.split('\n');
      for (const line of lines) {
        if (!line.startsWith('# PROGRESS:') && line) {
          csvContent += line + '\n';
        }
      }
    }

    // Use the final response.data as the definitive CSV content
    // (in case onDownloadProgress wasn't called or missed chunks)
    if (!csvContent || csvContent.length === 0) {
      csvContent = response.data
        .split('\n')
        .filter(line => !line.startsWith('# PROGRESS:') && line)
        .join('\n');
      if (csvContent) {
        csvContent += '\n';
      }
    }

    // Use headers extracted from XHR during streaming (more reliable than axios response.headers)
    const filename = headersFromXhr.filename || 'survey_responses.csv';
    const totalRecords = headersFromXhr.totalRecords || 0;

    // Create blob from CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    return { blob, filename, totalRecords };
  } catch (error) {
    loggingUtility.error('Error exporting survey responses:', error);
    throw error;
  }
};
